import type { PenAnchorPoint } from '@/lib/elements/types/pen-anchor-point'
import { closestPointOnPath } from '@/lib/elements/bezier-utils'
import type { PenSubpathRange } from '@/lib/elements/types/pen-subpath-range'
import type { NodeEditOverlayShapeGeom } from '../types/node-edit-overlay-props'
import type { PenHoverTarget } from '../types/node-edit-targets'

/** Screen-pixel hit radius for anchors and endpoints in pen-edit mode. */
const ANCHOR_HIT_PX = 12
/** Screen-pixel hit radius for segments in pen-edit mode. */
const SEGMENT_HIT_PX = 8

interface PenHoverHitTestInput {
  /** Pointer position in canvas space. */
  canvasX: number
  canvasY: number
  points: PenAnchorPoint[]
  isClosed: boolean
  subpaths: PenSubpathRange[]
  /** Shape origin the (shape-local) points are offset by. */
  ox: number
  oy: number
  scale: number
  /** Shape rotation (deg) and live geometry — the rotation pivots on the geometry's center. */
  rotation: number
  geom: NodeEditOverlayShapeGeom
}

/** What the pen-edit cursor is over: an open endpoint, any anchor, a segment, or nothing — in
 *  that priority order. */
export function hitTestPenHover({
  canvasX: rawMx,
  canvasY: rawMy,
  points: rawPts,
  isClosed,
  subpaths,
  ox,
  oy,
  scale,
  rotation: rot,
  geom,
}: PenHoverHitTestInput): PenHoverTarget {
  const n = rawPts.length
  const anchorR = ANCHOR_HIT_PX / scale
  const segR = SEGMENT_HIT_PX / scale
  const pts = rawPts

  // For a rotated shape the visual node positions are the pre-rotation canvas coords
  // rotated around the shape center. Un-rotate the mouse position into pre-rotation
  // canvas space so distance checks against (pt.x + ox, pt.y + oy) are correct.
  let mx = rawMx
  let my = rawMy

  if (rot) {
    const θ = (rot * Math.PI) / 180
    const cosR = Math.cos(θ)
    const sinR = Math.sin(θ)
    const rcx = geom.x + geom.width / 2
    const rcy = geom.y + geom.height / 2

    mx = rcx + (rawMx - rcx) * cosR + (rawMy - rcy) * sinR
    my = rcy - (rawMx - rcx) * sinR + (rawMy - rcy) * cosR
  }

  // Check open endpoints first — each OPEN subpath contributes its own start/end pair; a
  // single-subpath path (the traditional case) degenerates to exactly the old [0, n-1] check.
  const ranges = subpaths.length > 0 ? subpaths : [{ start: 0, end: n, isClosed }]

  for (const range of ranges) {
    if (range.isClosed || range.end <= range.start) continue

    for (const idx of [range.start, range.end - 1]) {
      const pt = pts[idx]
      const dx = pt.x + ox - mx
      const dy = pt.y + oy - my

      if (Math.sqrt(dx * dx + dy * dy) <= anchorR) {
        return { kind: 'endpoint', index: idx }
      }
    }
  }

  // Check any anchor (non-endpoint or closed path)
  for (let i = 0; i < n; i++) {
    const pt = pts[i]
    const dx = pt.x + ox - mx
    const dy = pt.y + oy - my

    if (Math.sqrt(dx * dx + dy * dy) <= anchorR) {
      return { kind: 'anchor', index: i }
    }
  }

  // Check segments (hitX/hitY are in pre-rotation canvas space, matching the SVG group)
  const seg = closestPointOnPath(pts, isClosed, ox, oy, mx, my, subpaths)

  if (seg && seg.dist <= segR) {
    return {
      kind: 'segment',
      segmentIndex: seg.segmentIndex,
      t: seg.t,
      hitX: seg.hitX,
      hitY: seg.hitY,
    }
  }

  return null
}
